import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const SQRT3 = Math.sqrt(3);

const TransformerTurnsRatioCalc = () => {
  const { dark } = useTheme();
  const [v1, setV1] = useState(138);
  const [v2, setV2] = useState(13.8);
  const [i2, setI2] = useState(1255);

  const calc = useMemo(() => {
    const a     = v2 > 0 ? v1 / v2 : 1;
    const a2    = a * a;
    const i1    = a > 0 ? i2 / a : 0;
    const s3ph  = (SQRT3 * v2 * i2) / 1000; // MVA
    const v1_ln = v1 / SQRT3;
    const v2_ln = v2 / SQRT3;
    return { a, a2, i1, s3ph, v1_ln, v2_ln };
  }, [v1, v2, i2]);

  const axisColor    = dark ? '#64748b' : '#94a3b8';
  const gridColor    = dark ? '#1e293b' : '#f1f5f9';
  const tooltipBg    = dark ? '#1e293b' : '#ffffff';
  const tooltipBorder = dark ? '#334155' : '#e2e8f0';

  const voltageData = [
    { name: 'V₁ (HV)', val: parseFloat(v1.toFixed(1)) },
    { name: 'V₂ (LV)', val: parseFloat(v2.toFixed(1)) },
  ];
  const currentData = [
    { name: 'I₁ (HV)', val: parseFloat(calc.i1.toFixed(1)) },
    { name: 'I₂ (LV)', val: parseFloat(i2.toFixed(1)) },
  ];

  return (
    <div data-testid="transformer-turns-ratio-calc" className="space-y-5">

      {/* Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">

        {/* V1 */}
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">V₁ — Primary V<sub>LL</sub> (kV)</label>
            <TeachingTooltip
              showIcon
              whatThis="The rated line-to-line voltage on the HV (primary) side of the transformer. For a 138/13.8 kV substation transformer, enter 138 kV here."
              tryThis="Try 138 kV primary / 13.8 kV secondary — a typical distribution substation step-down, giving turns ratio a = 10 exactly."
              whyMatters="The turns ratio a = V1/V2 determines how voltages, currents, and — critically — impedances scale across the transformer. Impedances scale by a², not a."
              connectedTo="→ Determines turns ratio a = V1/V2. Used in impedance referral as the squaring factor a²."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="v1-turns-input" type="range" min="1" max="765" step="0.1" value={v1}
              onChange={e => setV1(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={v1} step="0.1" min="1"
              onChange={e => setV1(Math.max(0.1, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-7">kV</span>
          </div>
        </div>

        {/* V2 */}
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">V₂ — Secondary V<sub>LL</sub> (kV)</label>
            <TeachingTooltip
              showIcon
              whatThis="The rated line-to-line voltage on the LV (secondary) side. For a 138/13.8 kV transformer this is 13.8 kV. This is the distribution voltage delivered to customers."
              tryThis="Change to 4.16 kV — a 138/4.16 kV transformer has a = 33.2, meaning impedances scale by a² = 1102. A small secondary reactance becomes enormous on the primary side."
              whyMatters="Small changes in V2 create large changes in the a² impedance scaling. Always verify nameplate voltages before per-unit fault analysis."
              connectedTo="→ Sets V2/V1 = 1/a for secondary phase voltage, and the a² impedance referral factor."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="v2-turns-input" type="range" min="0.1" max="345" step="0.1" value={v2}
              onChange={e => setV2(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={v2} step="0.1" min="0.1"
              onChange={e => setV2(Math.max(0.1, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-7">kV</span>
          </div>
        </div>

        {/* I2 */}
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">I₂ — Secondary Line Current (A)</label>
            <TeachingTooltip
              showIcon
              whatThis="The current drawn from the secondary (LV) terminals — measured by secondary-side CTs. Default 1255 A = full-load for 30 MVA at 13.8 kV (from S = √3·V·I)."
              tryThis="Enter 1255 A for 30 MVA at 13.8 kV — primary current I1 shows 125.5 A, showing the 10:1 current inversion produced by the turns ratio."
              whyMatters="Differential relay current compensation must match I1 and I2 from both CT sets. Incorrect turns ratio compensation causes false relay trips."
              connectedTo="→ Computes I1 = I2/a (primary current) and apparent power S = √3·V2·I2."
            />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="i2-turns-input" type="range" min="1" max="5000" step="1" value={i2}
              onChange={e => setI2(Number(e.target.value))} className="flex-1 accent-teal-600" />
            <input type="number" value={i2} step="1" min="0"
              onChange={e => setI2(Math.max(0, Number(e.target.value)))}
              className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none" />
            <span className="text-xs text-slate-500 w-4">A</span>
          </div>
        </div>
      </div>

      {/* Result cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div data-testid="turns-ratio-result" className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-teal-700 dark:text-teal-400 mb-1">Turns Ratio a</p>
          <p className="text-xl font-bold font-mono text-teal-900 dark:text-teal-100">{calc.a.toFixed(4)}</p>
          <p className="text-[10px] text-teal-600 dark:text-teal-500">V₁/V₂ = N₁/N₂</p>
        </div>
        <div data-testid="a-squared-result" className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-purple-700 dark:text-purple-400 mb-1">a² (imp. factor)</p>
          <p className="text-xl font-bold font-mono text-purple-900 dark:text-purple-100">{calc.a2.toFixed(2)}</p>
          <p className="text-[10px] text-purple-600 dark:text-purple-500">Z_pri = Z_sec × a²</p>
        </div>
        <div data-testid="i1-turns-result" className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">I₁ (primary)</p>
          <p className="text-xl font-bold font-mono text-slate-900 dark:text-white">{calc.i1.toFixed(1)}</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-500">A = I₂ ÷ a</p>
        </div>
        <div data-testid="apparent-power-turns-result" className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-1">S (apparent)</p>
          <p className="text-xl font-bold font-mono text-amber-900 dark:text-amber-100">{calc.s3ph.toFixed(2)}</p>
          <p className="text-[10px] text-amber-600 dark:text-amber-500">MVA = √3·V₂·I₂/1000</p>
        </div>
      </div>

      {/* Phase voltages note */}
      <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-md p-3 text-xs text-slate-600 dark:text-slate-400">
        <span className="font-semibold text-teal-700 dark:text-teal-400">Phase voltages (wye): </span>
        V₁_LN = {calc.v1_ln.toFixed(3)} kV (primary) ·· V₂_LN = {calc.v2_ln.toFixed(3)} kV (secondary) ·· Ratio = {(calc.v1_ln / calc.v2_ln).toFixed(4)} = a ✓
      </div>

      {/* Dual bar charts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">Voltage comparison (kV line-to-line)</p>
          <ResponsiveContainer width="100%" height={155}>
            <BarChart data={voltageData} margin={{ top: 14, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={{ stroke: axisColor }} tickFormatter={v => `${v}`} />
              <Tooltip contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 6, fontSize: 11 }} formatter={v => [`${v} kV`]} />
              <Bar dataKey="val" radius={[4, 4, 0, 0]}>
                <Cell fill="#6366f1" />
                <Cell fill="#0d9488" />
                <LabelList dataKey="val" position="top" style={{ fontSize: 10, fill: axisColor }} formatter={v => `${v} kV`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-center text-slate-400 dark:text-slate-600">V1/V2 = a = {calc.a.toFixed(4)}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">Current comparison (A) — inverted from voltage</p>
          <ResponsiveContainer width="100%" height={155}>
            <BarChart data={currentData} margin={{ top: 14, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={{ stroke: axisColor }} tickFormatter={v => `${v}`} />
              <Tooltip contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 6, fontSize: 11 }} formatter={v => [`${v} A`]} />
              <Bar dataKey="val" radius={[4, 4, 0, 0]}>
                <Cell fill="#6366f1" />
                <Cell fill="#0d9488" />
                <LabelList dataKey="val" position="top" style={{ fontSize: 10, fill: axisColor }} formatter={v => `${v} A`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-center text-slate-400 dark:text-slate-600">I1/I2 = 1/a = {(1 / calc.a).toFixed(4)} · HV voltage ↑ → HV current ↓</p>
        </div>
      </div>
    </div>
  );
};

export default TransformerTurnsRatioCalc;
